<script setup>
import { ref, onMounted, computed, watch, onUnmounted } from 'vue'
import {useRouter, useRoute} from 'vue-router'
import { useUserStore } from '@/stores/user'
import { useAppStore } from '@/stores/app'
import { useGeoStore } from '@/stores/geo'
import { useLayersStore } from '@/stores/layers'
import { useSearchStore } from '@/stores/search'
import { debounce } from 'lodash'
import {formatTimeFromNowNumeric} from '@/utils/datetime'

import { MapSource, MapLayer, MapImageMarker, FeatureCollectionRenderer } from '@/components/map'
import { toast } from '@/components/ui/toast'
import { mergePolygons } from '@/utils/geometry'
import { setSelectedArea, clearSelectedArea } from '@/composables/useAreaSelection'
import { createLayerId } from '@/components/layers/types'

const props = defineProps({
  map: {
    type: Object,
    required: true,
  },
  project: {
    type: Object,
    required: true,
  },
  activeTab: {
    type: String,
    required: true,
  },
})

const router = useRouter()
const route = useRoute()
const userStore = useUserStore()
const appStore = useAppStore()
const geoStore = useGeoStore()
const layersStore = useLayersStore()
const searchStore = useSearchStore()

const isLoggedIn = computed(() => appStore.isLoggedIn)
const settings = computed(() => userStore.settings)
const beacons = computed(() => geoStore.beacons)
const params = computed(() => searchStore.params)
const selectedRange = computed(() => layersStore.selectedRange)
const projectFilters = computed(() => geoStore.projectFilters)
const featureFetchStamp = computed(() => geoStore.featureFetchStamp)
const mapParamsKeys = ['zoom', 'center_lat', 'center_lng', 'south', 'north', 'east', 'west']
const boundsKeys = ['west', 'south', 'east', 'north']
const FETCH_PADDING = 0.5

const padBounds = ({ west, south, east, north }, factor) => {
  const dx = (east - west) * factor / 2
  const dy = (north - south) * factor / 2
  return {
    west: west - dx,
    south: Math.max(-85, south - dy),
    east: east + dx,
    north: Math.min(85, north + dy),
  }
}

const containsBounds = (outer, inner) =>
  outer.east >= outer.west && inner.east >= inner.west &&
  inner.west >= outer.west && inner.east <= outer.east &&
  inner.south >= outer.south && inner.north <= outer.north

let lastFetched = null
let inflight = null

const features = computed(() => geoStore.getVisibleFeatures)

// Features filtered to exclude current page feature
const filteredFeatures = computed(() => {
  if (!features.value || !features.value.features) {
    return { type: 'FeatureCollection', features: [] }
  }

  return {
    type: 'FeatureCollection',
    features: features.value.features.filter(
      feature => feature.properties.code !== currentPageFeatureCode.value
    )
  }
})

const filterParams = computed(() => {
  return params.value.filter((param) => !mapParamsKeys.includes(param.key))
})

const routeProjectId = computed(() => parseInt(route.params.project_id))

const isCurrentProject = computed(() => {
  const routeProjectId = Number(route.params.project_id)
  return !isNaN(routeProjectId) && props.project.project_id === routeProjectId
})
const currentPageFeatureCode = computed(() => route.params.featureCode)

const loadInitialBeacons = async () => {
  if (!routeProjectId.value) return
  await geoStore.fetchBeacons({
    project_id: routeProjectId.value,
  })
}

let loadingTimer = null
let loadRequestId = 0

const loadData = async () => {
  if (!routeProjectId.value || !isLoggedIn.value || !isCurrentProject.value) return

  const paramsMap = params.value.reduce((acc, param) => {
    acc[param.key] = param.value
    return acc
  }, {})

  const filterParamsMap = filterParams.value.reduce((acc, param) => {
    acc[param.key] = param.value
    return acc
  }, {})

  const cacheKey = JSON.stringify([routeProjectId.value, filterParamsMap, selectedRange.value, projectFilters.value, featureFetchStamp.value])
  const sameKey = lastFetched?.key === cacheKey
  const hasView = boundsKeys.every(k => typeof paramsMap[k] === 'number')
  const view = hasView ? Object.fromEntries(boundsKeys.map(k => [k, paramsMap[k]])) : null
  if (sameKey && view && lastFetched.bounds && containsBounds(lastFetched.bounds, view)) return
  if (inflight?.key === cacheKey && view && inflight.bounds && containsBounds(inflight.bounds, view)) return
  const bounds = view ? padBounds(view, FETCH_PADDING) : null

  const currentRequestId = ++loadRequestId
  inflight = { key: cacheKey, bounds }

  clearTimeout(loadingTimer)
  loadingTimer = setTimeout(() => {
    appStore.setIsLoadingData(true)
  }, 10000)

  // Use selectedRange from layers store (Navigator timeline) if available
  const [rangeStart, rangeEnd] = selectedRange.value || []

  const payload = {
    org_id: route.params.org_id,
    project_id: routeProjectId.value,
    shared: filterParamsMap['shared'],
    has_resources: filterParamsMap['has_resources'],
    startDate: rangeStart || filterParamsMap['start_date'],
    endDate: rangeEnd || filterParamsMap['end_date'],

    // map params
    south: bounds?.south ?? paramsMap['south'],
    north: bounds?.north ?? paramsMap['north'],
    east: bounds?.east ?? paramsMap['east'],
    west: bounds?.west ?? paramsMap['west'],

    cacheKey,
    reset: !sameKey,
    isStale: () => currentRequestId !== loadRequestId,
  }

  // Apply project filters (from ProjectFilters component)
  const filters = projectFilters.value
  if (filters) {
    if (filters.templateIds?.length > 0) {
      payload.template_ids = filters.templateIds.join(',')
    }
    if (filters.memberIds?.length > 0) {
      payload.member_ids = filters.memberIds.join(',')
    }
    if (filters.searchText?.trim()) {
      payload.search_text = filters.searchText
    }
    if (filters.isPublicData) {
      payload.is_public = true
    }
    if (filters.geometryTypes?.length > 0) {
      payload.geometry_types = filters.geometryTypes.join(',')
    }
    if (filters.geometries?.length > 0) {
      const merged = mergePolygons(filters.geometries)
      if (merged) {
        payload.geometry_filter = JSON.stringify(merged)
      }
    }
  }

  let ok = false
  try {
    ok = await geoStore.fetchMapFeatures(payload)
  } finally {
    if (currentRequestId === loadRequestId) {
      inflight = null
      if (ok) lastFetched = { key: cacheKey, bounds }
      clearTimeout(loadingTimer)
      appStore.setIsLoadingData(false)
    }
  }
}

const updateBounds = async () => {
  const bounds = props.map.getBounds()
  const center = props.map.getCenter()

  if (!bounds && !center) return

  const options = {
    center_lat: center.lat,
    center_lng: center.lng,
    zoom: props.map.getZoom(),
    south: bounds.getSouth(),
    north: bounds.getNorth(),
    east: bounds.getEast(),
    west: bounds.getWest()
  }

  searchStore.setParamsBulk(Object.entries(options).map(([key, value]) => ({ key, value })))

  await loadData()
}

const debouncedUpdateBounds = debounce(updateBounds, 400)

// Area selection scopes the monitoring tabs to one polygon — routes opt in
// via meta.areaSelection instead of this component knowing their names.
const selectionMode = computed(() => Boolean(route.meta?.areaSelection))

const onFeatureClick = (feature) => {
  if (!selectionMode.value) return
  const type = feature.geometry?.type
  if (type !== 'Polygon' && type !== 'MultiPolygon') return
  setSelectedArea({
    projectId: props.project.project_id,
    code: feature.properties?.code,
    name: feature.properties?.name,
  })
}

// Both this handler and the layer click decide from the same hit test, so
// their firing order does not matter: a polygon hit selects, a miss deselects.
const onMapClick = (e) => {
  if (!selectionMode.value) return
  const groupId = `project-${props.project.project_id}`
  const layers = [createLayerId(groupId, 'fill'), createLayerId(groupId, 'shape-centre')]
    .filter((id) => props.map.getLayer(id))
  const hits = layers.length ? props.map.queryRenderedFeatures(e.point, { layers }) : []
  if (!hits.length) clearSelectedArea()
}

const setListeners = () => {
  props.map.on('dragend', debouncedUpdateBounds)
  props.map.on('zoomend', debouncedUpdateBounds)
  props.map.on('click', onMapClick)
}

const removeListeners = () => {
  props.map.off('dragend', debouncedUpdateBounds)
  props.map.off('zoomend', debouncedUpdateBounds)
  props.map.off('click', onMapClick)
}

onMounted(async () => {
  if (!isCurrentProject.value) return
  await loadInitialBeacons()
  await loadData()
  setListeners()
})

onUnmounted(() => {
  removeListeners()
  clearTimeout(loadingTimer)
  lastFetched = null
  inflight = null
})

watch(
  () => isCurrentProject.value, async (newValue, oldValue) => {
    if (newValue) {
      await loadData()
      setListeners()
    }

    if (!newValue) {
      removeListeners()
    }
  }
)

watch(
  () => filterParams.value,
  async (newValue, oldValue) => {
    if (!oldValue) return

    await loadData()
  },
  { deep: true, immediate: true }
)

// Watch timeline range changes to update map features
watch(selectedRange, async () => {
  if (isCurrentProject.value) {
    await loadData()
  }
}, { deep: true })

// Watch project filters to update map features
watch(projectFilters, async () => {
  if (isCurrentProject.value) {
    await loadData()
  }
}, { deep: true })

watch(featureFetchStamp, async () => {
  if (isCurrentProject.value) {
    await loadData()
  }
})
</script>

<template>
<div v-if="isCurrentProject">
  <!-- Beacons (real-time user locations) -->
  <div v-if="beacons.length" :key="beacons.length">
    <MapImageMarker
      v-for="beacon in beacons"
      :key="beacon.properties.code"
      ping
      :label="`${beacon.properties.member_display_name} (~${formatTimeFromNowNumeric(beacon.properties.rel_ts, settings.tz)})`"
      :url="beacon.properties.member_avatar_url"
      :feature="beacon"
      :map="map"
    />
  </div>

  <!-- Project features using unified renderer -->
  <FeatureCollectionRenderer
    v-show="filteredFeatures.features.length > 0"
    :map="map"
    :features="filteredFeatures"
    :group-id="`project-${project.project_id}`"
    :enable-clustering="true"
    :enable-image-markers="true"
    :enable-age-based-styling="true"
    @feature-click="onFeatureClick"
  />
</div>

</template>
